import React, { useState } from 'react';
import { useTranslation } from '@/i18n';
import { FacadeImage } from '../FacadeImage';
import { ExpertiseCardProps } from './ExpertiseCard.types';
import { 
  generateTags, 
  getTagClass, 
  formatDate, 
  calculateProgress,
  calculateUrgency,
  getStatusColor 
} from './utils/tagRules';
import './ExpertiseCard.styles.css';

const formatCurrency = (amount?: number, currency = 'EUR', notAvailableText = 'N/A'): string => {
  if (!amount) return notAvailableText;
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency
  }).format(amount);
};

export const ExpertiseCard: React.FC<ExpertiseCardProps> = (props) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copyExpertiseNumber = (e: React.MouseEvent) => {
    e.stopPropagation();
    const ref = props.reference || props.id;
    const numbersOnly = ref.replace(/\D/g, '');
    navigator.clipboard.writeText(numbersOnly).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  const {
    id,
    reference,
    title,
    status,
    statusDisplay,
    stageId,
    expertName,
    responsableName,
    createdAt,
    dueDate,
    address,
    amount,
    currency,
    paid,
    perception,
    invoiceStatus,
    progress,
    onClick,
    onFileMaker,
    className = '',
    showFacadeImage = true,
    showProgress = true,
    showActions = true,
    showUrgency = true,
    showFileMaker = true,
    selected = false,
    disabled = false,
    loading = false
  } = props;

  // Determine if card should show as urgent (only if showUrgency is true)
  const isUrgent = showUrgency ? calculateUrgency(props) : false;
  
  const cardClasses = [
    'expertise-card',
    isUrgent ? 'urgent' : '',
    selected && 'selected',
    disabled && 'disabled',
    loading && 'loading',
    className
  ].filter(Boolean).join(' ');

  const handleClick = () => {
    if (!disabled && !loading && onClick) {
      onClick();
    }
  };

  // Use centralized functions for calculations
  const completionPercentage = calculateProgress(status, stageId, progress);

  // Use statusDisplay for badge text and color if available, otherwise fall back to status
  const displayStatus = statusDisplay || status;
  const statusColor = getStatusColor(displayStatus);

  // Format display status for better presentation
  const formattedStatus = displayStatus
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  
  // Generate tags using centralized rules, but filter out urgency-related tags if showUrgency is false
  let generatedTags = generateTags(props);
  if (!showUrgency) {
    // Filter out urgency-related tags (urgent, day counters)
    generatedTags = generatedTags.filter(tag => {
      const lowerTag = tag.toLowerCase();
      return !lowerTag.includes('urgent') && 
             !lowerTag.match(/^\d+j$/) && // Remove "5j" format
             !lowerTag.match(/^j-\d+$/i) && // Remove "J-3" format
             !lowerTag.includes('dépassé') &&
             !lowerTag.includes('nouveau') && // Also remove "Nouveau" tag for banque
             !lowerTag.includes('aujourd\'hui'); // Also remove "Aujourd'hui" tag for banque
    });
  }

  // Build address display
  const addressDisplay = address ? [
    address.street ? `${address.street}` : '',
    (address.postalCode || address.city) ? 
      `${address.postalCode || ''} ${address.city || ''}`.trim() : ''
  ].filter(Boolean) : [];

  // Build accessible label
  const accessibleLabel = `${title || t('expertise.card.realEstateExpertise')} - ${reference || id}${addressDisplay[0] ? `, ${addressDisplay[0]}` : ''}`;

  return (
    <article
      className={cardClasses}
      onClick={handleClick}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={accessibleLabel}
      aria-disabled={disabled}
      aria-busy={loading}
      aria-selected={selected}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled && !loading && onClick) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* Facade Image - Left Side */}
      {showFacadeImage && (
        <FacadeImage
          affaireId={id}
          className="card-facade-thumb"
        />
      )}

      {/* Main Info Section */}
      <div className="card-main-info">
        <div className="date-and-id-line">
          <span className="date-line">{formatDate(createdAt)}</span>
          <span className="affaire-id">#{reference || id}</span>
          <button
            className={`copy-id-btn ${copied ? 'copied' : ''}`}
            onClick={copyExpertiseNumber}
            title={copied ? t('common.copied') : t('common.copyToClipboard')}
            aria-label={t('common.copyToClipboard')}
          >
            {copied ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            )}
          </button>
        </div>
        <h3 className="expertise-card-title" id={`expertise-title-${id}`}>
          {title || t('expertise.card.realEstateExpertise')}
        </h3>
        <div className="card-address">
          {addressDisplay[0] && <div className="address-line-1">{addressDisplay[0]}</div>}
          {addressDisplay[1] && <div className="address-line-2">{addressDisplay[1]}</div>}
        </div>
      </div>

      {/* Meta Section - Price, Tags, Responsable */}
      <div className="card-meta">
        <span className="card-price">{formatCurrency(amount, currency, t('expertise.card.notAvailable'))}</span>
        <div className="card-tags">
          {generatedTags.slice(0, 4).map((tag, index) => {
            const tagClass = getTagClass(tag);
            return (
              <span key={index} className={`tag ${tagClass}`}>{tag}</span>
            );
          })}
        </div>
        {responsableName && (
          <div className="card-responsable">
            <span className="responsable-label">Resp:</span>
            <span className="responsable-value">{responsableName}</span>
          </div>
        )}
      </div>

      {/* Billing Section */}
      <div className="card-billing">
        {paid && <span className="card-paid-badge">{t('expertise.card.paid')}</span>}
        {perception && <span className="card-billing-info">{perception}</span>}
        {invoiceStatus === 'emise' && <span className="card-invoice-badge">{t('expertise.card.invoiceIssued')}</span>}
      </div>

      {/* Flex spacer to push progress and actions to the right */}
      <div className="card-flex-spacer"></div>

      {/* Progress Section */}
      {showProgress && (
        <div className="card-progress-info">
          <div className="expert-info">
            <div className="expert-label">{t('expertise.card.assignedExpert')}</div>
            <div className="expert-name">{expertName || ''}</div>
          </div>
          
          {/* RV Date if available */}
          {dueDate && (
            <div className="rv-date-corner">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              <span>{formatDate(dueDate)}</span>
            </div>
          )}
          
          <div className="progress-section">
            <div className="progress-label">
              <span>{t('expertise.card.progress')}</span>
              <span className="progress-percentage">{completionPercentage}%</span>
            </div>
            <div className="progress-bar" data-percentage={completionPercentage}>
              <div
                className="progress-fill"
                style={{ width: `${completionPercentage}%` }}
              />
              {/* Stage markers - aligned with the 11 stages */}
              <div className="stage-markers">
                <span className="stage-marker" style={{ left: '9%' }} title={t('expertise.stages.officeToEncode')} />
                <span className="stage-marker" style={{ left: '18%' }} title={t('expertise.stages.officeToPlan')} />
                <span className="stage-marker" style={{ left: '27%' }} title={t('expertise.stages.appointmentPending')} />
                <span className="stage-marker" style={{ left: '36%' }} title={t('expertise.stages.expertToVisit')} />
                <span className="stage-marker" style={{ left: '45%' }} title={t('expertise.stages.expertToComplete')} />
                <span className="stage-marker" style={{ left: '54%' }} title={t('expertise.stages.officeToValuate')} />
                <span className="stage-marker" style={{ left: '64%' }} title={t('expertise.stages.officeToFinish')} />
                <span className="stage-marker" style={{ left: '73%' }} title={t('expertise.stages.waitingDocsInfo')} />
                <span className="stage-marker" style={{ left: '82%' }} title={t('expertise.stages.waitingPayment')} />
                <span className="stage-marker" style={{ left: '91%' }} title={t('expertise.stages.waitingConfirmation')} />
                <span className="stage-marker" style={{ left: '100%' }} title={t('expertise.stages.toTransmit')} />
              </div>
            </div>
          </div>

          {/* Status Badge - below progress */}
          <span className={`status-badge status-${statusColor}`}>
            {formattedStatus}
          </span>
        </div>
      )}

      {/* Right Section - Actions */}
      <div className="card-actions">
        {showActions && (
          <div className="card-cta-section">
            {/* Details button */}
            <div className="cta-button-group">
              <div className="cta-arrow" onClick={(e) => {
                e.stopPropagation();
                onClick && onClick();
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14"/>
                  <path d="m12 5 7 7-7 7"/>
                </svg>
              </div>
              <span className="cta-label">{t('expertise.card.detail')}</span>
            </div>
            
            {/* FileMaker button */}
            {showFileMaker && onFileMaker && (
              <div className="cta-button-group">
                <div className="cta-arrow cta-arrow-fm" onClick={(e) => {
                  e.stopPropagation();
                  onFileMaker();
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="7" width="18" height="14" rx="2" ry="2"/>
                    <rect x="8" y="7" width="8" height="5"/>
                    <line x1="4" y1="17" x2="20" y2="17"/>
                    <line x1="8" y1="17" x2="8" y2="21"/>
                    <line x1="12" y1="17" x2="12" y2="21"/>
                    <line x1="16" y1="17" x2="16" y2="21"/>
                  </svg>
                </div>
                <span className="cta-label">{t('expertise.card.fileMaker')}</span>
              </div>
            )}
            
          </div>
        )}
      </div>
    </article>
  );
};